import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Clock, Calendar, CheckCircle2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getEmployeeColor, getEmployeeColorLight } from '@/lib/colorUtils';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const JOURS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const genererCreneaux = () => {
  const creneaux = [];
  for (let h = 8; h < 18; h++) {
    for (let m = 0; m < 60; m += 15) {
      creneaux.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  return creneaux;
};
const CRENEAUX = genererCreneaux();

/**
 * CalendrierRdvEmbed
 * 
 * Calendrier de rendez-vous embarquable dans un formulaire.
 * Affiche le calendrier mensuel + créneaux horaires.
 * Quand l'utilisateur clique un créneau → onSlotSelect(date, heure, employe_id, employe_nom)
 * 
 * Props:
 *   onSlotSelect(date: string, heure: string, employe_id: string, employe_nom: string)
 *   selectedSlot: { date, heure, employe_id, employe_nom } | null
 *   defaultEmployeId: string (optionnel - pré-sélectionner un employé)
 */
export function CalendrierRdvEmbed({ onSlotSelect, selectedSlot, defaultEmployeId }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [filtreEmploye, setFiltreEmploye] = useState(defaultEmployeId || 'tous');

  const [indisponibilites, setIndisponibilites] = useState([]);
  const [rendezVous, setRendezVous] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  useEffect(() => {
    if (defaultEmployeId && defaultEmployeId !== filtreEmploye) {
      setFiltreEmploye(defaultEmployeId);
    }
  }, [defaultEmployeId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const [indispoRes, rdvRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/indisponibilites?year=${year}&month=${month}`),
        axios.get(`${API_URL}/api/crm/rendez-vous?year=${year}&month=${month}`),
        axios.get(`${API_URL}/api/employees`),
      ]);

      if (indispoRes.status === 200) {
        const data = indispoRes.data;
        setIndisponibilites(data.indisponibilites || []);
      }
      if (rdvRes.status === 200) {
        const data = rdvRes.data;
        // ✅ Stocker tous les RDVs mais filtrer à l'affichage par représentants seulement
        setRendezVous(data.rendez_vous || []);
      }
      if (empRes.status === 200) {
        const data = empRes.data;
        // Cat H — `e.role` = rôle DE L'EMPLOYÉ dans les données (représentants seulement), pas RBAC utilisateur connecté. Filtre données.
        // Filtrer uniquement les représentants pour les RDV de soumission
        const reps = (data.employees || []).filter(e =>
          e.type_employe === 'representant' || e.role === 'representant'  // E3b-whitelist : catégorie de données
        );
        setEmployees(reps);
        // ✅ Si 1 seul représentant, le sélectionner automatiquement
        if (reps.length === 1 && filtreEmploye === 'tous') {
          setFiltreEmploye(reps[0].id);
        }
      }
    } catch (err) {
      console.error('Erreur chargement calendrier:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  };

  const getRdvForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    const repIds = new Set(employees.map(e => e.id));
    // ✅ Toujours filtrer aux représentants seulement
    const rdvsFiltres = filtreEmploye === 'tous'
      ? rendezVous.filter(r => repIds.has(r.employe_id))
      : rendezVous.filter(r => r.employe_id === filtreEmploye);
    return rdvsFiltres.filter(r => r.date === dateStr);
  };

  const creneauEstDisponible = (date, heure) => {
    const dateStr = date.toISOString().split('T')[0];
    const repIds = new Set(employees.map(e => e.id));
    // ✅ Vérifier disponibilité uniquement pour les représentants
    const rdvsAVerifier = filtreEmploye === 'tous'
      ? rendezVous.filter(r => repIds.has(r.employe_id))
      : rendezVous.filter(r => r.employe_id === filtreEmploye);

    const rdvExiste = rdvsAVerifier.some((r) => {
      if (r.date !== dateStr) return false;
      if (r.type_rdv === 'soumission') {
        const [h, m] = r.heure_debut.split(':').map(Number);
        const minutesDebut = h * 60 + m;
        const minutesFin = minutesDebut + 60;
        const [hTest, mTest] = heure.split(':').map(Number);
        const minutesTest = hTest * 60 + mTest;
        return minutesTest >= minutesDebut && minutesTest < minutesFin;
      }
      return r.heure_debut === heure;
    });

    return !rdvExiste;
  };

  const isSlotSelected = (date, heure) => {
    if (!selectedSlot || !date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return selectedSlot.date === dateStr && selectedSlot.heure === heure;
  };

  const handleSlotClick = (date, heure) => {
    const employe = employees.find((e) => e.id === filtreEmploye);
    const employe_id = filtreEmploye !== 'tous' ? filtreEmploye : '';
    const employe_nom =
      filtreEmploye !== 'tous' && employe ? `${employe.prenom} ${employe.nom}` : '';
    const dateStr = date.toISOString().split('T')[0];
    onSlotSelect(dateStr, heure, employe_id, employe_nom);
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-3">
      {/* Header: navigation + filtre employé */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
              setCurrentDate(d);
            }}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-sm w-36 text-center">
            {MOIS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const d = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
              setCurrentDate(d);
            }}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Select value={filtreEmploye} onValueChange={setFiltreEmploye}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Tous les employés" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les employés</SelectItem>
            {employees.map((emp) => (
              <SelectItem key={emp.id} value={emp.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getEmployeeColor(emp.id) }}
                  />
                  {emp.prenom} {emp.nom}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selected slot summary */}
      {selectedSlot && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>
            <strong>RDV sélectionné :</strong>{' '}
            {new Date(selectedSlot.date + 'T12:00:00').toLocaleDateString('fr-CA', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}{' '}
            à <strong>{selectedSlot.heure}</strong>
            {selectedSlot.employe_nom && (
              <> — {selectedSlot.employe_nom}</>
            )}
          </span>
          <button
            type="button"
            className="ml-auto text-green-600 hover:text-red-500 text-xs underline"
            onClick={() => onSlotSelect(null, null, null, null)}
          >
            Effacer
          </button>
        </div>
      )}

      {/* Main grid: calendar + slots */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Monthly calendar */}
        <div className="lg:col-span-2 border rounded-lg p-3 bg-white">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {JOURS.map((j) => (
              <div key={j} className="text-center text-xs font-semibold text-gray-500">
                {j}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} className="aspect-square" />;
              const isToday = day.toDateString() === new Date().toDateString();
              const isSelected =
                selectedDate && day.toDateString() === selectedDate.toDateString();
              const rdvs = getRdvForDate(day);
              const hasSelectedRdv =
                selectedSlot && selectedSlot.date === day.toISOString().split('T')[0];

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => setSelectedDate(day)}
                  className={`aspect-square border rounded-lg p-1 text-xs transition-all ${
                    isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  } ${isSelected ? 'ring-2 ring-blue-500' : ''} ${
                    hasSelectedRdv ? 'ring-2 ring-green-500 bg-green-50' : ''
                  }`}
                >
                  <p className={`font-semibold ${isToday ? 'text-blue-600' : ''}`}>
                    {day.getDate()}
                  </p>
                  {rdvs.length > 0 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mx-auto mt-0.5" />
                  )}
                  {hasSelectedRdv && (
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mx-auto mt-0.5" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time slots */}
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b">
            <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {selectedDate
                ? selectedDate.toLocaleDateString('fr-CA', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })
                : 'Sélectionnez une date'}
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto p-2">
            {!selectedDate ? (
              <p className="text-xs text-gray-400 text-center py-6">
                Cliquez sur une date dans le calendrier
              </p>
            ) : loading ? (
              <p className="text-xs text-gray-400 text-center py-6">Chargement...</p>
            ) : (
              <div className="space-y-1">
                {/* Existing RDVs */}
                {getRdvForDate(selectedDate).length > 0 && (
                  <div className="mb-2">
                    {getRdvForDate(selectedDate).map((rdv) => (
                      <div
                        key={rdv.id}
                        className="text-xs px-2 py-1 rounded mb-1 border-l-2"
                        style={{
                          borderLeftColor: getEmployeeColor(rdv.employe_id),
                          backgroundColor: getEmployeeColorLight(rdv.employe_id),
                        }}
                      >
                        <span className="font-medium">{rdv.heure_debut}</span> — {rdv.titre}
                      </div>
                    ))}
                    <div className="border-t my-2" />
                  </div>
                )}

                {/* Available slots */}
                <div className="grid grid-cols-2 gap-1">
                  {CRENEAUX.map((creneau) => {
                    const disponible = creneauEstDisponible(selectedDate, creneau);
                    const selected = isSlotSelected(selectedDate, creneau);
                    return (
                      <button
                        key={creneau}
                        type="button"
                        disabled={!disponible}
                        onClick={() => disponible && handleSlotClick(selectedDate, creneau)}
                        className={`text-xs px-2 py-1 rounded border transition-all ${
                          selected
                            ? 'bg-green-500 text-white border-green-500 font-semibold'
                            : disponible
                            ? 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer'
                            : 'bg-gray-100 text-gray-300 border-transparent cursor-not-allowed'
                        }`}
                      >
                        {creneau}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!selectedSlot && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Cliquez sur une date puis sur un créneau disponible pour prendre un rendez-vous
        </p>
      )}
    </div>
  );
}
